import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight, Download } from "lucide-react";
import { getPaymentStatus, API } from "@/lib/api";

const MAX_POLLS = 8;

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error

  const poll = useCallback(async (attempt) => {
    if (!sessionId) { setState("error"); return; }
    if (attempt >= MAX_POLLS) { setState("pending"); return; }
    try {
      const data = await getPaymentStatus(sessionId);
      if (data.payment_status === "paid") { setState("paid"); return; }
      if (data.status === "expired" || data.payment_status === "failed") { setState("error"); return; }
      setTimeout(() => poll(attempt + 1), 2000);
    } catch (e) {
      setTimeout(() => poll(attempt + 1), 2000);
    }
  }, [sessionId]);

  useEffect(() => { poll(0); }, [poll]);

  return (
    <div data-testid="payment-success-page" className="pt-32 min-h-screen">
      <div className="max-w-lg mx-auto px-6 py-24 text-center">
        {state === "checking" && (
          <>
            <Loader2 className="h-14 w-14 text-golden-500 animate-spin mx-auto mb-8" />
            <h1 className="font-serif text-4xl text-sage-100 mb-4">Confirming your purchase…</h1>
            <p className="text-sage-300">Hang tight while we verify your payment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="h-16 w-16 text-golden-500 mx-auto mb-8" />
            <h1 className="font-serif text-5xl text-sage-100 mb-4">You're all set.</h1>
            <p className="text-sage-300 mb-10">Thank you for your purchase. Download your toolkit below, and we've also emailed you a copy.</p>
            <a href={`${API}/download/${sessionId}`} data-testid="success-download-btn" className="group inline-flex items-center gap-2 bg-golden-500 text-navy font-semibold px-8 py-4 rounded-sm transition-colors duration-300 hover:bg-golden-600">
              <Download className="h-4 w-4" /> Download your toolkit
            </a>
            <div className="mt-6">
              <a href="/toolkits" data-testid="success-back-toolkits" className="text-golden-500 font-semibold inline-flex items-center gap-2">
                Browse more toolkits <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="h-14 w-14 text-golden-500 mx-auto mb-8" />
            <h1 className="font-serif text-4xl text-sage-100 mb-4">Almost there.</h1>
            <p className="text-sage-300 mb-10">Your payment is still processing. You'll receive a confirmation email once it clears.</p>
            <a href="/toolkits" className="text-golden-500 font-semibold">Back to toolkits</a>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="h-16 w-16 text-sage-400 mx-auto mb-8" />
            <h1 className="font-serif text-4xl text-sage-100 mb-4">We couldn't confirm that.</h1>
            <p className="text-sage-300 mb-10">No charge may have gone through. Please try again or reach out if you need help.</p>
            <a href="/toolkits" data-testid="error-back-toolkits" className="group inline-flex items-center gap-2 bg-golden-500 text-navy font-semibold px-8 py-4 rounded-sm transition-colors duration-300 hover:bg-golden-600">
              Back to toolkits <ArrowRight className="h-4 w-4" />
            </a>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
